import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LinkItem } from "@/components/navigation/Nav";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ARCHIVE_SEARCH_SHORTCUT_LABEL,
  useArchiveSearchDialog,
} from "@/components/customer/archive/archiveSearchDialogContext";

interface NavItemProps {
  pathname: string;
  link: LinkItem;
  // When rendered inside the grouped staff section, the section label + accent
  // border carry the "staff only" signal, so the per-item orange is dropped.
  suppressStaffColor?: boolean;
}

export function isActive(pathname: string, target: string) {
  return pathname === target || pathname.startsWith(`${target}/`);
}

export default function NavItem({
  pathname,
  link,
  suppressStaffColor = false,
}: NavItemProps) {
  const { t } = useTranslation();
  const { open: openArchiveSearch } = useArchiveSearchDialog();
  const active = isActive(pathname, link.target);

  const staffColor =
    link.staffOnly && !active && !suppressStaffColor
      ? { color: "rgb(249 115 22)" }
      : undefined;

  const linkContent = (
    <div
      className={cn(
        buttonVariants({
          variant: active ? "secondary" : "ghost",
          size: "sm",
        }),
        "justify-start w-full",
        {
          "opacity-50 cursor-not-allowed": link.disabled,
        },
      )}
      style={
        active
          ? {
              backgroundColor: "var(--color-secondary)",
              color: "var(--color-secondary-foreground)",
            }
          : staffColor
      }
    >
      <link.icon className="mr-2 h-4 w-4" />
      {t(link.title)}
      {link.statusDot && (
        <span className="relative flex h-2 w-2 shrink-0 ml-1.5">
          {link.online && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          )}
          {/* Solid, theme-independent color: a translucent/inherited-color dot
              washed out on the active item's dark-blue background before. */}
          <span
            className={cn(
              "relative z-10 inline-flex rounded-full h-2 w-2",
              link.online ? "bg-green-500" : "bg-slate-400",
            )}
          />
        </span>
      )}
      {link.label && (
        <span
          className={cn("ml-auto", {
            "text-background dark:text-white": active,
          })}
        >
          {link.label}
        </span>
      )}
    </div>
  );

  if (link.disabled && link.disabledTooltip) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div>{linkContent}</div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{t(link.disabledTooltip)}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (link.disabled) {
    return <div>{linkContent}</div>;
  }

  if (link.action === "archiveSearch") {
    return (
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <button type="button" onClick={() => openArchiveSearch()} className="w-full">
            {linkContent}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="flex items-center gap-4">
          {t(link.title)}
          <span className="text-muted-foreground">{ARCHIVE_SEARCH_SHORTCUT_LABEL}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Link to={link.target} className="w-full">
      {linkContent}
    </Link>
  );
}
